import { useRecoilValue, useSetRecoilState } from 'recoil';
import { pushModalSelector } from '@/stores/modalStore';
import { unlockUISelector } from '@/stores/appStore';
import { useCallback } from 'react';
import { Toast } from '@/ui/toast';
import { useTracker } from '@/core/tracking';
import { useLocalization } from '@/services/localizationService';
import { useUser } from '@/services/userService';
import * as Sentry from '@sentry/nextjs';
import settingsState from '@/stores/settingsStore';
import { usePaymentService } from '@/layout/appWrapper/ServiceProvider';
import { useConsent } from '@/features/userConsent';
import YourPluginKeyModal from '@/features/account/accountModal/pluginKeySection/modals/YourPluginKeyModal';
import { selectedPaymentStrategyState } from '@/stores/onboardingStore';
import { PaymentStrategiesIds } from '@/services/paymentService';

export const usePayPalButton = (unlockedExports?: number) => {
  const pushModal = useSetRecoilState(pushModalSelector);
  const unlockUI = useSetRecoilState(unlockUISelector);
  const selectedPaymentStrategy = useRecoilValue(selectedPaymentStrategyState);

  const { createPaypalSubscription, upgradePaypalSubscription } =
    usePaymentService();

  const { shouldShowMarketingConsentModal } = useConsent();

  const { track } = useTracker();
  const { data } = useLocalization();

  const { data: user, mutate: mutateUser } = useUser();
  const settings = useRecoilValue(settingsState);

  const onSubscriptionApprove = useCallback(
    async (subscriptionId: string) => {
      await createPaypalSubscription(subscriptionId, unlockedExports);
      mutateUser();

      unlockUI({});

      // the PayPal hover screen lasts some seconds before disappearing
      setTimeout(() => {
        pushModal({
          id: 'subscriptionToast',
          Modal: Toast,
          message: data.onboarding.congratsMember,
          duration: 2000,
          onEnd: () => shouldShowMarketingConsentModal(),
          onClose: () =>
            settings.values.psPlugin &&
            selectedPaymentStrategy?.id === PaymentStrategiesIds.Business &&
            pushModal({
              id: 'generate-plugin-key-modal',
              Modal: YourPluginKeyModal,
            }),
        });
      }, 2400);
    },
    [
      createPaypalSubscription,
      settings.values.psPlugin,
      unlockedExports,
      mutateUser,
      unlockUI,
      pushModal,
      data.onboarding.congratsMember,
      shouldShowMarketingConsentModal,
      selectedPaymentStrategy?.id,
    ]
  );

  const onUpgradeApprove = useCallback(
    async (subscriptionId?: string | null) => {
      if (!subscriptionId) {
        Sentry.captureException(
          new Error("PayPal's data.subscriptionID not set")
        );
        return;
      }

      await upgradePaypalSubscription(subscriptionId);
      mutateUser();

      // the PayPal hover screen lasts some seconds before disappearing
      setTimeout(() => {
        pushModal({
          id: 'subscriptionToast',
          Modal: Toast,
          message: data.account.congratsBusiness,
          duration: 2000,
          onClose: () =>
            settings.values.psPlugin &&
            pushModal({
              id: 'generate-plugin-key-modal',
              Modal: YourPluginKeyModal,
            }),
        });
      }, 2400);
    },
    [
      upgradePaypalSubscription,
      mutateUser,
      pushModal,
      data.account.congratsBusiness,
      settings.values.psPlugin,
    ]
  );

  return {
    pushModal,
    unlockUI,
    onSubscriptionApprove,
    onUpgradeApprove,
    track,
    webUserId: user?.webUserId,
  };
};
